import React, { useState, useEffect } from 'react';

/**
 * Enhanced Scraping Progress Component with Component-wise Status
 * Shows detailed progress for each part of the scraping and RAG process
 */
const ScrapingProgress = ({ 
  isActive, 
  currentStage, 
  overallProgress, 
  websiteUrl,
  onProgressUpdate 
}) => {
  const [componentStatus, setComponentStatus] = useState({
    urlValidation: { status: 'pending', progress: 0, message: 'Waiting to start...' },
    webScraper: { status: 'pending', progress: 0, message: 'Waiting for URL validation...' },
    contentProcessor: { status: 'pending', progress: 0, message: 'Waiting for scraper...' },
    aiEmbedding: { status: 'pending', progress: 0, message: 'Waiting for content processing...' },
    knowledgeBase: { status: 'pending', progress: 0, message: 'Waiting for embeddings...' },
    ragEngine: { status: 'pending', progress: 0, message: 'Waiting for knowledge base...' }
  });

  // Map backend stages to component updates
  const mapStageToComponents = (stage, progress, message) => {
    const updates = {};
    
    switch (stage) {
      case 'starting':
        updates.urlValidation = { 
          status: 'processing', 
          progress: Math.min(progress, 100), 
          message: message || `Validating ${websiteUrl}...` 
        };
        break;

      case 'scraping':
        updates.urlValidation = { 
          status: 'completed', 
          progress: 100, 
          message: 'URL validated successfully' 
        };
        updates.webScraper = { 
          status: 'processing', 
          progress: Math.min(progress, 100), 
          message: message || 'Extracting content...' 
        };
        break;

      case 'analyzing':
        updates.urlValidation = { status: 'completed', progress: 100, message: 'URL validated successfully' };
        updates.webScraper = { status: 'completed', progress: 100, message: 'Content extracted successfully' };
        updates.contentProcessor = { 
          status: 'processing', 
          progress: Math.min(progress, 100), 
          message: message || 'Analyzing content...' 
        };
        break;

      case 'embedding':
        updates.urlValidation = { status: 'completed', progress: 100, message: 'URL validated successfully' };
        updates.webScraper = { status: 'completed', progress: 100, message: 'Content extracted successfully' };
        updates.contentProcessor = { status: 'completed', progress: 100, message: 'Content processed successfully' };
        updates.aiEmbedding = { 
          status: 'processing', 
          progress: Math.min(progress, 100), 
          message: message || 'Creating AI embeddings...' 
        };
        break;

      case 'indexing':
        updates.urlValidation = { status: 'completed', progress: 100, message: 'URL validated successfully' };
        updates.webScraper = { status: 'completed', progress: 100, message: 'Content extracted successfully' };
        updates.contentProcessor = { status: 'completed', progress: 100, message: 'Content processed successfully' };
        updates.aiEmbedding = { status: 'completed', progress: 100, message: 'Embeddings created successfully' };
        updates.knowledgeBase = { 
          status: 'processing', 
          progress: Math.min(progress, 100), 
          message: message || 'Storing in knowledge base...' 
        };
        break;

      case 'completing':
        updates.urlValidation = { status: 'completed', progress: 100, message: 'URL validated successfully' };
        updates.webScraper = { status: 'completed', progress: 100, message: 'Content extracted successfully' };
        updates.contentProcessor = { status: 'completed', progress: 100, message: 'Content processed successfully' };
        updates.aiEmbedding = { status: 'completed', progress: 100, message: 'Embeddings created successfully' };
        updates.knowledgeBase = { status: 'completed', progress: 100, message: 'Content stored successfully' };
        updates.ragEngine = { 
          status: 'processing', 
          progress: Math.min(progress, 100), 
          message: message || 'Finalizing RAG engine...' 
        };
        break;

      case 'success':
        // Mark all components as completed
        updates.urlValidation = { status: 'completed', progress: 100, message: 'URL validated successfully' };
        updates.webScraper = { status: 'completed', progress: 100, message: 'Content extracted successfully' };
        updates.contentProcessor = { status: 'completed', progress: 100, message: 'Content processed successfully' };
        updates.aiEmbedding = { status: 'completed', progress: 100, message: 'Embeddings created successfully' };
        updates.knowledgeBase = { status: 'completed', progress: 100, message: 'Content stored successfully' };
        updates.ragEngine = { status: 'completed', progress: 100, message: 'RAG engine ready!' };
        break;

      case 'error':
        // Mark current processing component as failed
        const currentComponent = getCurrentComponent(stage);
        if (currentComponent) {
          updates[currentComponent] = { status: 'failed', progress: 0, message: message || 'Process failed' };
        }
        break;
    }
    
    return updates;
  };

  // Update component status based on current stage and progress
  useEffect(() => {
    if (!isActive) {
      // Reset all to pending when not active
      setComponentStatus({
        urlValidation: { status: 'pending', progress: 0, message: 'Ready to start...' },
        webScraper: { status: 'pending', progress: 0, message: 'Ready to scrape...' },
        contentProcessor: { status: 'pending', progress: 0, message: 'Ready to process...' },
        aiEmbedding: { status: 'pending', progress: 0, message: 'Ready to create embeddings...' },
        knowledgeBase: { status: 'pending', progress: 0, message: 'Ready to store content...' },
        ragEngine: { status: 'pending', progress: 0, message: 'Ready to enable search...' }
      });
      return;
    }

    // Update components based on stage and progress
    const updates = mapStageToComponents(currentStage, overallProgress);
    if (Object.keys(updates).length > 0) {
      setComponentStatus(prev => ({
        ...prev,
        ...updates
      }));
    }
    
    // Notify parent of progress updates if callback provided
    if (onProgressUpdate && typeof onProgressUpdate === 'function') {
      onProgressUpdate(overallProgress);
    }
  }, [currentStage, overallProgress, isActive, websiteUrl, onProgressUpdate]);

  const getCurrentComponent = (stage) => {
    switch (stage) {
      case 'starting': return 'urlValidation';
      case 'scraping': return 'webScraper';
      case 'analyzing': return 'contentProcessor';
      case 'completing': return 'knowledgeBase';
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'processing': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'failed': return 'text-red-600 bg-red-50 border-red-200';
      case 'pending': return 'text-gray-500 bg-gray-50 border-gray-200';
      default: return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'processing': return '⚡';
      case 'failed': return '❌';
      case 'pending': return '⏳';
      default: return '⏳';
    }
  };

  const ProgressBar = ({ progress, status }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-500 ${
          status === 'completed' ? 'bg-green-500' :
          status === 'processing' ? 'bg-blue-500' :
          status === 'failed' ? 'bg-red-500' : 'bg-gray-300'
        }`}
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );

  const components = [
    { key: 'urlValidation', label: 'URL Validation', icon: '🔗' },
    { key: 'webScraper', label: 'Web Scraper', icon: '🕷️' },
    { key: 'contentProcessor', label: 'Content Processor', icon: '⚙️' },
    { key: 'aiEmbedding', label: 'AI Embedding', icon: '🧠' },
    { key: 'knowledgeBase', label: 'Knowledge Base', icon: '📚' },
    { key: 'ragEngine', label: 'RAG Engine', icon: '🔍' }
  ];

  if (!isActive) {
    return null;
  }

  return (
    <div className="w-full max-w-4xl mx-auto mt-6 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Overall Progress Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium text-gray-900">🚀 Knowledge Base Creation Progress</h3>
          <span className="text-sm text-gray-500">{overallProgress}% Complete</span>
        </div>
        <ProgressBar progress={overallProgress} status={overallProgress === 100 ? 'completed' : 'processing'} />
      </div>

      {/* Component-wise Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {components.map((component) => {
          const status = componentStatus[component.key];
          return (
            <div
              key={component.key}
              className={`p-4 border rounded-lg transition-all duration-300 ${getStatusColor(status.status)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <span className="mr-2 text-lg">{component.icon}</span>
                  <span className="font-medium text-sm">{component.label}</span>
                </div>
                <span className="text-lg">{getStatusIcon(status.status)}</span>
              </div>
              
              <ProgressBar progress={status.progress} status={status.status} />
              
              <p className="text-xs mt-2 text-gray-600">{status.message}</p>
            </div>
          );
        })}
      </div>

      {/* Processing Info */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          <span className="text-sm text-blue-800">
            Processing: <strong>{websiteUrl}</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScrapingProgress;